import type { AnalysisTilesProps } from '@/components/posture-analysis/AnalysisTiles';

export interface PostureMobileSplitOptions {
  isNarrowViewport: boolean;
  showAnalysisPanel: boolean;
  hasAnyAnalysisContent: boolean;
}

/** Minimum scrollable analysis body below view tabs + segment row (mobile split). */
export const MOBILE_SPLIT_ANALYSIS_BODY_MIN_PX = 120;

/** Base viewport-height share for the pinned photo in mobile split (self-assessment). */
export const MOBILE_SPLIT_PHOTO_HEIGHT_FRACTION = 0.53;

/** When immersive chrome is hidden, allow slightly more photo height. */
export const MOBILE_SPLIT_PHOTO_HEIGHT_FRACTION_CHROME_HIDDEN = 0.55;

/**
 * Practitioner posture: use 55% of the base photo height cap so the View Report tray
 * gets plenty of vertical space (≈29% of viewport) alongside the docked toolbar (~140px chrome).
 */
export const MOBILE_SPLIT_PRACTITIONER_PHOTO_HEIGHT_SCALE = 0.55;

/**
 * Self-assessment mobile: scale down the pinned photo so the Report | Posture tray and
 * bottom CTA stay visible without scrolling past the posture metrics.
 */
export const MOBILE_SPLIT_SELF_ASSESSMENT_PHOTO_HEIGHT_SCALE = 0.58;

/** Mobile split: pinned Pic + segmented View Report | Posture below. */
export function shouldUsePostureMobileSplit(options: PostureMobileSplitOptions): boolean {
  return (
    options.isNarrowViewport &&
    options.showAnalysisPanel &&
    options.hasAnyAnalysisContent
  );
}

export function postureAnalysisTilesLayout(
  options: PostureMobileSplitOptions,
): AnalysisTilesProps['layout'] {
  return shouldUsePostureMobileSplit(options) ? 'segmented' : 'accordion';
}

export interface MobileSplitCanvasCapGateOptions {
  useMobileSplit: boolean;
  isSelfAssessment: boolean;
  hasAnyCapturedImage: boolean;
  unassignedCount: number;
}

/**
 * Practitioner: always cap canvas when split is active.
 * Self-assessment: cap only after captures exist and the assign queue is empty
 * (during assign queue the page uses flex/document scroll without a hard cap).
 */
export function shouldApplyMobileSplitCanvasCap(
  options: MobileSplitCanvasCapGateOptions,
): boolean {
  if (!options.useMobileSplit) return false;
  if (!options.isSelfAssessment) return true;
  return options.hasAnyCapturedImage && options.unassignedCount === 0;
}

export interface MobileSplitCanvasCapOptions {
  useMobileSplit: boolean;
  shouldCapCanvas: boolean;
  windowHeight: number;
  isChromeHidden: boolean;
  /** Practitioner routes scale the photo cap to 75% so the report tray gains height. */
  isPractitioner?: boolean;
}

/**
 * Caps inline canvas height on mobile split so the View Report | Posture tray stays visible
 * and scrollable below the pinned Pic. Uses width-first sizing in canvas-size.ts.
 */
export function computeMobileSplitCanvasCapPx(
  options: MobileSplitCanvasCapOptions,
): number | undefined {
  if (!options.useMobileSplit || !options.shouldCapCanvas || options.windowHeight <= 0) {
    return undefined;
  }

  const baseFraction = options.isChromeHidden
    ? MOBILE_SPLIT_PHOTO_HEIGHT_FRACTION_CHROME_HIDDEN
    : MOBILE_SPLIT_PHOTO_HEIGHT_FRACTION;
  const fraction = options.isPractitioner
    ? baseFraction * MOBILE_SPLIT_PRACTITIONER_PHOTO_HEIGHT_SCALE
    : baseFraction * MOBILE_SPLIT_SELF_ASSESSMENT_PHOTO_HEIGHT_SCALE;
  return Math.max(160, Math.round(options.windowHeight * fraction));
}

/** Flex child classes so the analysis tray receives remaining height and can scroll internally. */
export function postureMobileSplitAnalysisColumnClass(useMobileSplit: boolean): string {
  return useMobileSplit ? 'flex-1 min-h-[160px] basis-0 touch-pan-y flex flex-col' : '';
}
